import { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn.js';
import { scrollToSection } from '@/lib/scrollToSection.js';
import {
  PRIMARY_NAV,
  isNavLinkActive,
  isNavGroupActive,
} from '@/data/navigation.js';
import { SiteLogo } from './SiteLogo.jsx';

/* Nav split — left half ends at Speakers, right half starts at Agenda */
const LEFT_NAV  = PRIMARY_NAV.slice(0, 5);
const RIGHT_NAV = PRIMARY_NAV.slice(5);

const navTextClass = 'text-xs xl:text-[13px] font-semibold uppercase tracking-[0.12em]';

/* Transparent / default scrolled link class */
const navLinkClass = (active) => cn(
  'px-2.5 xl:px-3 py-1.5 rounded-full transition-colors whitespace-nowrap',
  navTextClass,
  active
    ? 'bg-brand-100 text-brand-700 dark:bg-transparent dark:text-amber-400'
    : 'text-ink-700 dark:text-ink-300 hover:text-brand-700 hover:bg-brand-50 dark:hover:text-amber-400 dark:hover:bg-white/10',
);

/* Scrolled-state link class — rich navy in light mode; dark mode unchanged (white + amber) */
const scrolledNavLinkClass = (active) => cn(
  'px-2.5 xl:px-3 py-1.5 rounded-full transition-colors whitespace-nowrap',
  navTextClass,
  active
    ? 'bg-brand-100 text-brand-700 dark:bg-transparent dark:text-amber-400'
    : 'text-ink-700 hover:text-brand-700 hover:bg-brand-50 dark:text-white/85 dark:hover:text-amber-400',
);

const dropdownLinkClass = (active) => cn(
  'block px-3 py-2.5 rounded-xl text-sm transition-colors',
  active
    ? 'bg-white/20 text-white dark:bg-white/10 dark:text-amber-400'
    : 'text-ink-100 hover:bg-white/15 hover:text-white dark:text-ink-100 dark:hover:bg-white/10',
);

/* ─── Wat Arun (Temple of Dawn, Bangkok) — refined silhouette icon ─── */
function WatArunIcon({ className }) {
  return (
    <svg
      viewBox="0 0 80 80"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      {/* ═════════════════════════════════════════════
          CENTRAL MAIN PRANG (Phra Prang)
      ═════════════════════════════════════════════ */}

      {/* Crowning finial (yod) — orb + needle */}
      <circle cx="40" cy="2.2" r="1.4" />
      <path d="M40,3.6 L41.2,8.8 L38.8,8.8 Z" />

      {/* Decorative discs */}
      <ellipse cx="40" cy="9.3" rx="2.3" ry="0.7" />
      <ellipse cx="40" cy="10.7" rx="1.7" ry="0.55" />

      {/* Lotus bud (chada) — signature Thai temple element */}
      <path d="M37.6,11.2 Q40,9.4 42.4,11.2 L41.8,15 L38.2,15 Z" />

      {/* Bell / anda — bulging crown */}
      <path d="M36.2,15 Q35.2,18 38,19.2 L42,19.2 Q44.8,18 43.8,15 Z" />

      {/* Square neck collar */}
      <rect x="37.8" y="19.2" width="4.4" height="1.5" />

      {/* TIER 1 — top, with redented (stepped-shoulder) profile */}
      <path d="M34.2,20.7 L45.8,20.7 L46.8,21.8 L45.6,23 L47.2,25.2 L32.8,25.2 L34.4,23 L33.2,21.8 Z" />

      {/* TIER 2 */}
      <path d="M30.8,25.2 L49.2,25.2 L50.6,26.7 L49.2,28.2 L51,30.8 L29,30.8 L30.8,28.2 L29.4,26.7 Z" />

      {/* TIER 3 — widest tier */}
      <path d="M26.8,30.8 L53.2,30.8 L55,32.6 L53.2,34.4 L55.5,37.4 L24.5,37.4 L26.8,34.4 L25,32.6 Z" />

      {/* Main body (rued nai) with redented profile */}
      <path d="M24,37.4 L56,37.4 L57.8,39.2 L56,41 L56,45.8 L24,45.8 L24,41 L22.2,39.2 Z" />

      {/* ═════════════════════════════════════════════
          LEFT SATELLITE PRANG (mondop)
      ═════════════════════════════════════════════ */}

      <circle cx="11" cy="25.4" r="0.9" />
      <path d="M11,26.3 L11.8,29.4 L10.2,29.4 Z" />
      <ellipse cx="11" cy="29.8" rx="1.5" ry="0.45" />
      <path d="M9.7,30.2 Q11,29.2 12.3,30.2 L12,32.4 L10,32.4 Z" />
      <path d="M9,32.4 Q8.4,34 10,34.7 L12,34.7 Q13.6,34 13,32.4 Z" />
      <rect x="9.7" y="34.7" width="2.6" height="0.9" />

      {/* Tier 1 (left satellite) */}
      <path d="M8.4,35.6 L13.6,35.6 L14.2,36.4 L13.6,37.2 L14.6,38.8 L7.4,38.8 L8.4,37.2 L7.8,36.4 Z" />

      {/* Tier 2 (left satellite) */}
      <path d="M6.4,38.8 L15.6,38.8 L16.6,40 L15.6,41.2 L17,43.4 L5,43.4 L6.4,41.2 L5.4,40 Z" />

      {/* Body (left satellite) */}
      <path d="M4.2,43.4 L17.8,43.4 L18.8,44.6 L17.8,45.8 L17.8,49 L4.2,49 L4.2,45.8 L3.2,44.6 Z" />

      {/* ═════════════════════════════════════════════
          RIGHT SATELLITE PRANG (mondop)
      ═════════════════════════════════════════════ */}

      <circle cx="69" cy="25.4" r="0.9" />
      <path d="M69,26.3 L69.8,29.4 L68.2,29.4 Z" />
      <ellipse cx="69" cy="29.8" rx="1.5" ry="0.45" />
      <path d="M67.7,30.2 Q69,29.2 70.3,30.2 L70,32.4 L68,32.4 Z" />
      <path d="M67,32.4 Q66.4,34 68,34.7 L70,34.7 Q71.6,34 71,32.4 Z" />
      <rect x="67.7" y="34.7" width="2.6" height="0.9" />

      {/* Tier 1 (right satellite) */}
      <path d="M66.4,35.6 L71.6,35.6 L72.2,36.4 L71.6,37.2 L72.6,38.8 L65.4,38.8 L66.4,37.2 L65.8,36.4 Z" />

      {/* Tier 2 (right satellite) */}
      <path d="M64.4,38.8 L73.6,38.8 L74.6,40 L73.6,41.2 L75,43.4 L63,43.4 L64.4,41.2 L63.4,40 Z" />

      {/* Body (right satellite) */}
      <path d="M62.2,43.4 L75.8,43.4 L76.8,44.6 L75.8,45.8 L75.8,49 L62.2,49 L62.2,45.8 L61.2,44.6 Z" />

      {/* ═════════════════════════════════════════════
          TERRACED BASE PLATFORMS
      ═════════════════════════════════════════════ */}

      <rect x="3"  y="49"   width="74" height="4"   rx="0.6" />
      <rect x="1"  y="53"   width="78" height="5"   rx="0.6" />
      <rect x="0"  y="58"   width="80" height="6"   rx="0.6" />
      <rect x="0"  y="64"   width="80" height="6.5" rx="0.6" />

      {/* Porcelain-mosaic accent dots — Wat Arun's signature surface */}
      <circle cx="14" cy="60.8" r="0.55" />
      <circle cx="22" cy="60.8" r="0.55" />
      <circle cx="30" cy="60.8" r="0.55" />
      <circle cx="38" cy="60.8" r="0.55" />
      <circle cx="46" cy="60.8" r="0.55" />
      <circle cx="54" cy="60.8" r="0.55" />
      <circle cx="62" cy="60.8" r="0.55" />
      <circle cx="70" cy="60.8" r="0.55" />

      {/* Subtle stair-step accent at center base */}
      <rect x="36" y="70.5" width="8" height="2" rx="0.4" />
      <rect x="34" y="72.5" width="12" height="2" rx="0.4" />
      <rect x="32" y="74.5" width="16" height="2" rx="0.4" />
    </svg>
  );
}

function NavItemLink({ to, className, children, onClick }) {
  const location = useLocation();
  const active = isNavLinkActive(to, location);

  if (to.includes('#')) {
    const [path, hashId = ''] = to.split('#');

    const handleClick = (e) => {
      onClick?.(e);
      if (!hashId) return;

      const onTargetPage =
        location.pathname === path ||
        (path === '/call-for-papers' && location.pathname === '/cfp');

      if (onTargetPage) {
        window.setTimeout(() => scrollToSection(hashId, 'smooth'), 0);
      }
    };

    return (
      <Link to={to} onClick={handleClick} className={className(active)}>
        {children}
      </Link>
    );
  }

  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onClick}
      className={({ isActive }) => className(isActive || active)}
    >
      {children}
    </NavLink>
  );
}

/* Reusable dropdown — parent label links to the main page; hover reveals sub-links */
function NavDropdown({ item, openMenu, setOpenMenu, linkClass }) {
  const location = useLocation();
  const isOpen = openMenu === item.label;
  const groupActive = isNavGroupActive(item, location);
  const menuId = `nav-menu-${item.label.toLowerCase()}`;
  const wide = item.label === 'Committee' || item.label === 'CFP';
  const primaryTo = item.to ?? item.children[0]?.to?.split('#')[0] ?? '/';

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpenMenu(item.label)}
      onMouseLeave={() => setOpenMenu(null)}
    >
      <NavItemLink
        to={primaryTo}
        onClick={() => setOpenMenu(null)}
        className={(active) =>
          cn(linkClass(groupActive || isOpen || active), 'inline-flex items-center gap-0.5')
        }
      >
        {item.label}
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={cn('transition-transform', isOpen && 'rotate-180')}
        />
      </NavItemLink>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={menuId}
            role="menu"
            aria-label={item.label}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'absolute left-1/2 -translate-x-1/2 top-full mt-2 rounded-2xl shadow-glass overflow-hidden',
              'bg-ink-800/75 dark:bg-ink-900/95 backdrop-blur-2xl border border-ink-700/40 dark:border-ink-700/60',
              wide ? 'w-80' : 'w-64',
            )}
          >
            <div className="p-2">
              {item.children.map((c) => (
                <NavItemLink
                  key={c.to}
                  to={c.to}
                  className={dropdownLinkClass}
                  onClick={() => setOpenMenu(null)}
                >
                  {c.label}
                </NavItemLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const closeMobile = () => setMobileOpen(false);
  const onDarkHero = !scrolled;

  /* Link class for the transparent (hero) state */
  const heroNavLinkClass = (active) =>
    cn(
      'px-2.5 xl:px-3 py-1.5 rounded-full transition-colors whitespace-nowrap',
      navTextClass,
      active
        ? 'bg-brand-600 text-white dark:bg-transparent dark:text-amber-400'
        : 'text-ink-900 hover:text-brand-700 hover:bg-white/60 dark:text-white/90 dark:hover:text-amber-400',
    );

  /* Render a single item for the full PRIMARY_NAV (transparent state) */
  function renderHeroNavItem(item) {
    if (item.children) {
      return (
        <NavDropdown
          key={item.label}
          item={item}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          linkClass={heroNavLinkClass}
        />
      );
    }
    return (
      <NavItemLink key={item.to} to={item.to} className={heroNavLinkClass}>
        {item.label}
      </NavItemLink>
    );
  }

  /* Render a single item for split nav halves (scrolled state) */
  function renderScrolledNavItem(item) {
    if (item.children) {
      return (
        <NavDropdown
          key={item.label}
          item={item}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          linkClass={scrolledNavLinkClass}
        />
      );
    }
    return (
      <NavItemLink key={item.to} to={item.to} className={scrolledNavLinkClass}>
        {item.label}
      </NavItemLink>
    );
  }

  const iconBtnClass =
    'w-10 h-10 rounded-full flex items-center justify-center transition-colors text-white hover:bg-white/10';

  return (
    <motion.header
      role="banner"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'fixed top-0 inset-x-0 z-50 transition-all duration-300',
        onDarkHero
          ? 'bg-gradient-to-b from-ink-950/80 to-transparent border-b border-white/5'
          : 'bg-[#0d1b2e]/96 backdrop-blur-xl border-b border-white/10 shadow-[0_2px_24px_rgba(0,0,0,0.45)]',
      )}
    >
      {/* ═══════════════════════════════════════════════
          TRANSPARENT / DARK-HERO STATE  (unchanged)
      ═══════════════════════════════════════════════ */}
      {onDarkHero && (
        <div className="container flex items-center justify-between h-[4.25rem] lg:h-[4.75rem]">
          <SiteLogo />

          {/* Full nav — xl screens */}
          <nav className="hidden xl:flex items-center gap-0.5" aria-label="Primary">
            {PRIMARY_NAV.map(renderHeroNavItem)}
          </nav>

          {/* Compact nav — lg only (no xl) */}
          <nav className="hidden lg:flex xl:hidden items-center gap-0.5" aria-label="Primary compact">
            {PRIMARY_NAV.filter((item) => !item.children).map((item) => (
              <NavItemLink key={item.to} to={item.to} className={heroNavLinkClass}>
                {item.label}
              </NavItemLink>
            ))}
            <NavItemLink to="/call-for-papers" className={heroNavLinkClass}>CFP</NavItemLink>
            <NavItemLink to="/committees" className={heroNavLinkClass}>Committee</NavItemLink>
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/registration" className="hidden md:inline-flex btn-accent px-5 py-2.5 text-[15px]">
              Register
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              className={cn('xl:hidden', iconBtnClass)}
            >
              {mobileOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════
          SCROLLED STATE — full-width dark navy bar
          with split nav and centre Wat Arun emblem
      ═══════════════════════════════════════════════ */}
      {!onDarkHero && (
        <div className="container flex items-center justify-between h-[4.25rem] lg:h-[4.75rem]">
          {/* Logo — identical component/size as transparent state */}
          <SiteLogo />

          {/* ── Split nav with Wat Arun centre piece (xl only) ── */}
          <div className="hidden xl:flex flex-1 items-center justify-center">
            {/* Left half */}
            <nav className="flex items-center gap-0" aria-label="Primary left">
              {LEFT_NAV.map(renderScrolledNavItem)}
            </nav>

            {/* Centre Wat Arun emblem */}
            <div className="shrink-0 mx-3 flex items-center justify-center">
              <div className="relative w-14 h-14 rounded-full flex items-center justify-center bg-brand-50 border-2 border-brand-500/70 shadow-[0_0_18px_rgba(28,68,156,0.25),inset_0_0_8px_rgba(28,68,156,0.08)] dark:bg-[#0a1628] dark:border-amber-400/80 dark:shadow-[0_0_18px_rgba(251,191,36,0.4),inset_0_0_8px_rgba(251,191,36,0.1)]">
                <WatArunIcon className="w-9 h-9 text-brand-600 dark:text-amber-400" />
              </div>
            </div>

            {/* Right half */}
            <nav className="flex items-center gap-0" aria-label="Primary right">
              {RIGHT_NAV.map(renderScrolledNavItem)}
            </nav>
          </div>

          {/* Compact nav — lg only (no xl) */}
          <nav className="hidden lg:flex xl:hidden items-center gap-0.5" aria-label="Primary compact scrolled">
            {PRIMARY_NAV.filter((item) => !item.children).map((item) => (
              <NavItemLink key={item.to} to={item.to} className={scrolledNavLinkClass}>
                {item.label}
              </NavItemLink>
            ))}
          </nav>

          {/* Actions — same as transparent state */}
          <div className="flex items-center gap-2">
            <Link to="/registration" className="hidden md:inline-flex btn-accent px-5 py-2.5 text-[15px]">
              Register
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              className={cn('xl:hidden', iconBtnClass)}
            >
              {mobileOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          MOBILE NAV (shared — both states)
      ═══════════════════════════════════════════ */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="mobile-nav"
            role="navigation"
            aria-label="Mobile primary"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden border-t border-ink-200/50 dark:border-ink-800/50 bg-surface/95 dark:bg-ink-950/95 backdrop-blur-xl overflow-hidden"
          >
            <div className="container py-4 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
              {PRIMARY_NAV.map((item) => {
                if (item.children) {
                  const primaryTo = item.to ?? item.children[0]?.to?.split('#')[0] ?? '/';
                  return (
                    <div key={item.label}>
                      <NavItemLink
                        to={primaryTo}
                        onClick={closeMobile}
                        className={(active) => cn(
                          'block px-3 py-3 rounded-xl text-sm font-semibold transition-colors',
                          active
                            ? 'bg-brand-100 text-brand-700 dark:bg-transparent dark:text-amber-400'
                            : 'text-ink-700 hover:bg-brand-50 hover:text-brand-700 dark:text-ink-200 dark:hover:bg-ink-800',
                        )}
                      >
                        {item.label}
                      </NavItemLink>
                      {item.children.map((c) => (
                        <NavItemLink
                          key={c.to}
                          to={c.to}
                          onClick={closeMobile}
                          className={(active) => cn(
                            'block pl-6 pr-3 py-2.5 rounded-xl text-sm transition-colors',
                            active
                              ? 'bg-brand-100 text-brand-700 dark:bg-transparent dark:text-amber-400'
                              : 'text-ink-700 hover:bg-brand-50 hover:text-brand-700 dark:text-ink-200 dark:hover:bg-ink-800',
                          )}
                        >
                          {c.label}
                        </NavItemLink>
                      ))}
                    </div>
                  );
                }
                return (
                  <NavItemLink
                    key={item.to}
                    to={item.to}
                    onClick={closeMobile}
                    className={(active) => cn(
                      'block px-3 py-3 rounded-xl text-sm font-medium transition-colors',
                      active
                        ? 'bg-brand-100 text-brand-700 dark:bg-transparent dark:text-amber-400'
                        : 'text-ink-700 hover:bg-brand-50 hover:text-brand-700 dark:text-ink-200 dark:hover:bg-ink-800',
                    )}
                  >
                    {item.label}
                  </NavItemLink>
                );
              })}
              <Link
                to="/registration"
                className="btn-accent mt-3 text-[15px] px-6 py-3.5"
                onClick={closeMobile}
              >
                Register Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
